import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './design-system/typography.css';
import './design-system/styles.css';
import App from './App';
import './design-system/website-theme.css';
import { syncDocumentTheme } from './design-system/theme';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { Buffer } from 'buffer';
import process from 'process/browser.js';
import { initAnalytics, GTM_ID } from './utils/analytics';
import { PaymentStatusProvider } from './contexts/PaymentStatusContext';
import { UnifiedAuthProvider } from './contexts/UnifiedAuthContext'; // <-- UNIFIED AUTH SYSTEM
import { getAuth0RedirectOrigin } from './utils/auth0Origin';
import {
  AUTH0_CACHE_LOCATION,
  AUTH0_USE_REFRESH_TOKENS,
  AUTH0_USE_REFRESH_TOKENS_FALLBACK,
} from './utils/auth0PersistenceConfig';
import { ensureAuthStorageCapacity } from './utils/browserStorage';
import { getCanonicalLocalFrontendRedirect } from './utils/localFrontendOrigin';

syncDocumentTheme();

const isPrerenderRuntime = Boolean(
  (window as Window & { __BOTSPOT_PRERENDER__?: boolean }).__BOTSPOT_PRERENDER__,
);
const shouldLoadExternalAnalytics =
  !window.navigator.webdriver || window.__BOTSPOT_PLAYWRIGHT_EXTERNAL_ANALYTICS__ === true;

const canonicalLocalRedirect =
  process.env.NODE_ENV === 'development' && !isPrerenderRuntime
    ? getCanonicalLocalFrontendRedirect(
        window.location,
        process.env.REACT_APP_LOCAL_FRONTEND_ORIGIN,
      )
    : null;

if (canonicalLocalRedirect) {
  window.location.replace(canonicalLocalRedirect);
}

const getUnknownMessage = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
    return value.message;
  }
  return '';
};

// Suppress ResizeObserver errors globally
if (typeof window !== 'undefined') {
  // Polyfill to suppress ResizeObserver errors
  const originalResizeObserver = window.ResizeObserver;
  if (originalResizeObserver) {
    class WrappedResizeObserver implements ResizeObserver {
      private readonly observer: ResizeObserver;

      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
          requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              // Suppress ResizeObserver loop errors
              const message = getUnknownMessage(error);
              if (
                !message ||
                !message.includes('ResizeObserver loop completed with undelivered notifications')
              ) {
                throw error;
              }
            }
          });
        };
        this.observer = new originalResizeObserver(wrappedCallback);
      }

      observe(target: Element, options?: ResizeObserverOptions) {
        this.observer.observe(target, options);
      }

      unobserve(target: Element) {
        this.observer.unobserve(target);
      }

      disconnect() {
        this.observer.disconnect();
      }
    }

    window.ResizeObserver = WrappedResizeObserver;
    // Copy static methods if any exist
    Object.setPrototypeOf(window.ResizeObserver, originalResizeObserver);
    Object.defineProperty(window.ResizeObserver, 'toString', {
      value: () => 'function ResizeObserver() { [native code] }',
    });
  }

  // Additional global error handlers
  const resizeObserverErrorMatcher = (value: unknown) => {
    const msg = getUnknownMessage(value);
    return (
      typeof msg === 'string' &&
      (msg.includes('ResizeObserver loop completed with undelivered notifications') ||
        msg.includes('ResizeObserver loop limit exceeded'))
    );
  };

  const originalError = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (resizeObserverErrorMatcher(message) || resizeObserverErrorMatcher(error)) {
      return true;
    }
    if (originalError) {
      return originalError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  window.addEventListener(
    'error',
    (event) => {
      if (resizeObserverErrorMatcher(event.message) || resizeObserverErrorMatcher(event.error)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  window.addEventListener('unhandledrejection', function (event) {
    if (resizeObserverErrorMatcher(event.reason)) {
      event.preventDefault();
    }
  });
}

// Make Buffer and process available globally if required by your app.
// Remove these lines if not needed.
if (!window.Buffer) {
  window.Buffer = Buffer;
}
if (!window.process) {
  window.process = process;
}

// Dynamically load Facebook Pixel using env var
const loadFacebookPixel = () => {
  if (isPrerenderRuntime || !shouldLoadExternalAnalytics) return;
  if (process.env.REACT_APP_FACEBOOK_PIXEL_ID) {
    const fbScript = document.createElement('script');
    fbScript.innerHTML = `
      !function(f,b,e,v,n,t,s) {
        if(f.fbq)return; n=f.fbq=function(){
          n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments)
        };
        if(!f._fbq)f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0'; n.queue=[];
        t=b.createElement(e); t.async=!0; t.src=v;
        s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${process.env.REACT_APP_FACEBOOK_PIXEL_ID}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(fbScript);

    const fbNoScript = document.createElement('noscript');
    fbNoScript.innerHTML = `<img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${process.env.REACT_APP_FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(fbNoScript);
  }
};

loadFacebookPixel();

// Initialize Analytics Providers (including GTM)
if (!isPrerenderRuntime && shouldLoadExternalAnalytics) initAnalytics();

// Add GTM noscript tag if GTM_ID is set
if (GTM_ID && !isPrerenderRuntime && shouldLoadExternalAnalytics) {
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

// Environment is available via process.env.REACT_APP_ENV if needed for debugging

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Static tags serve crawlers before JavaScript. Remove them before Helmet
// mounts so runtime navigation always has one canonical metadata owner.
document.querySelectorAll('[data-static-seo="true"]').forEach((node) => node.remove());

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
const auth0RedirectOrigin = getAuth0RedirectOrigin(window.location);

const isSafeInternalReturnTo = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.startsWith('/\\');

const getSafeAuth0ReturnTo = (returnTo: unknown): string =>
  isSafeInternalReturnTo(returnTo) ? returnTo : '/';

// Auth0 callback handling and BotSpot custom JWT hydration both need a small
// amount of first-party storage. Real production profiles can fill localStorage
// with derived chart/indicator caches, so recover that space before Auth0 mounts.
ensureAuthStorageCapacity('localStorage');

const app = (
  <React.StrictMode>
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        redirect_uri: auth0RedirectOrigin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation={AUTH0_CACHE_LOCATION}
      useRefreshTokens={AUTH0_USE_REFRESH_TOKENS}
      useRefreshTokensFallback={AUTH0_USE_REFRESH_TOKENS_FALLBACK}
      onRedirectCallback={(appState) => {
        // Use setTimeout to ensure React Router is ready
        setTimeout(() => {
          const returnTo = getSafeAuth0ReturnTo(appState?.returnTo);
          window.history.replaceState({}, document.title, returnTo);
          // Dispatch a popstate event to ensure React Router detects the change
          window.dispatchEvent(new PopStateEvent('popstate'));
        }, 100);
      }}
    >
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <UnifiedAuthProvider>
          <PaymentStatusProvider>
            <App />
          </PaymentStatusProvider>
        </UnifiedAuthProvider>
      </BrowserRouter>
    </Auth0Provider>
  </React.StrictMode>
);

if (rootElement.dataset.prerendered === 'true') {
  // Prerenders capture the settled anonymous app, while first client render
  // starts in auth-loading state. Mount fresh to avoid invalid hydration.
  rootElement.replaceChildren();
  delete rootElement.dataset.prerendered;
}
if (!canonicalLocalRedirect) {
  ReactDOM.createRoot(rootElement).render(app);
}
